import { FormikAutocomplete } from '../../../input/FormikAutocomplete/FormikAutocomplete';
import { FormikTextField } from '../../../input/FormikTextField/FormikTextField';
import { DurationUnit } from '../../../shared/interfaces/date';
import {
  convertMillisToNearestUnits,
  hoursToMillis,
  millisToHours,
  millisToMinutes,
  minutesToMillis
} from '../../../shared/utils/date';
import {
  EligibleFrequencyUnitsInCron,
  PipelineSettingsFeatures,
  PipelineSettingsFeaturesData,
  SyncFrequencyTypeFeatureOption,
  SyncFrequencyTypes
} from '../../interfaces/pipeline-settings-config';
import { PipelineSettingsFormFields } from '../../PipelineSettings/interfaces/pipeline-settings-form-state';
import styles from './SyncFrequencyWithCronFormField.module.scss';

export const frequencyTypeFieldMap: {
  [key in EligibleFrequencyUnitsInCron]: { fieldTitle: string; fieldProps: object };
} = {
  [EligibleFrequencyUnitsInCron.HOURS]: {
    fieldTitle: 'Sync at every',
    fieldProps: {
      dataId: 'custom-frequency-hours',
      label: 'Hours',
      placeholder: 'Hours',
      type: 'number',
      sx: { width: '200px' },
      disableDecimal: true,
      required: true,
      component: FormikTextField,
      convertFrom: millisToHours,
      convertTo: hoursToMillis,
      errorMessageClassName: styles.hourTextFieldHelperText
    }
  },
  [EligibleFrequencyUnitsInCron.MINUTES]: {
    fieldTitle: 'Sync at every',
    fieldProps: {
      dataId: 'custom-frequency-minutes',
      label: 'Minutes',
      placeholder: 'Minutes',
      type: 'number',
      sx: { width: '200px' },
      disableDecimal: true,
      required: true,
      component: FormikAutocomplete,
      convertFrom: millisToMinutes,
      convertTo: minutesToMillis,
      errorMessageClassName: styles.hourTextFieldHelperText
    }
  },
  [EligibleFrequencyUnitsInCron.CRON]: {
    fieldTitle: 'CRON expression',
    fieldProps: {
      name: PipelineSettingsFormFields.SYNC_FREQUENCY_IN_CRON,
      dataId: 'custom-frequency-cron',
      type: 'string',
      sx: { width: '200px' },
      component: FormikTextField,
      label: 'CRON expression',
      placeholder: '* * * * *',
      errorMessageClassName: styles.hourTextFieldHelperText
    }
  }
};

export const getSyncFrequencyTypeFromFeatures = (
  options: SyncFrequencyTypeFeatureOption[],
  value: number,
  syncFrequencyType: string
) => {
  // Return the option directly if the sync type is CRON
  if (syncFrequencyType === 'CRON') {
    return options.find(option => option.value === syncFrequencyType);
  }

  // If there's only one option, return it directly
  if (options.length === 1) {
    return options[0];
  }

  // Determine the unit and value of the provided sync frequency
  const { value: convertedValue, unit } = convertMillisToNearestUnits(value);

  // Match the correct option based on the unit and its minimum threshold
  switch (unit) {
    case DurationUnit.HOURS:
    case DurationUnit.DAYS:
      if (convertedValue >= 1) {
        return options.find(option => option.unit === EligibleFrequencyUnitsInCron.HOURS);
      }
      break;

    case DurationUnit.MINUTES:
      if (convertedValue >= 1) {
        return options.find(option => option.unit === EligibleFrequencyUnitsInCron.MINUTES);
      }
      break;
    default:
      return options[0];
  }

  return options[0];
};

export const getSyncFrequencyWithCronFeatureOptions = (features: PipelineSettingsFeaturesData) => {
  return {
    options: features.features[PipelineSettingsFeatures.SYNC_FREQUENCY_TYPE]!.options,
    optionsForMins: features.features[PipelineSettingsFeatures.SYNC_FREQUENCY]!.options,
    custom_option: {
      [SyncFrequencyTypes.CRON]:
        features.features[PipelineSettingsFeatures.SYNC_FREQUENCY_IN_CRON]?.custom_option ??
        undefined,
      [SyncFrequencyTypes.FIXED]:
        features.features[PipelineSettingsFeatures.SYNC_FREQUENCY]?.custom_option ?? undefined
    },
    banner: features.features[PipelineSettingsFeatures.SYNC_FREQUENCY_TYPE]?.banner ?? null
  };
};
